import { useEffect, useState, type FormEvent } from 'react';

import { Scramble } from '../brand/Scramble';
import { api, errorMessage } from '../lib/api';
import { fmtDate, fmtDay, PERIODS, todayIso, type Period } from '../lib/format';
import type { SectionInfo } from '../lib/reading';
import { quote, quoteLines } from '../lib/pricing';
import type { Plan, ProposalMeta, SelectionItem, Slot } from '../lib/types';
import { Dialog } from './Dialog';

const Done = ({ title, text, onClose }: { title: string; text: string; onClose: () => void }) => (
  <div className="done">
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="32" r="30" />
      <path d="M20 33l8 8 16-17" />
    </svg>
    <h3>{title}</h3>
    <p>{text}</p>
    <button type="button" className="btn btn-ghost" onClick={onClose} style={{ marginTop: '.5rem' }}>
      Voltar para a proposta
    </button>
  </div>
);

// ------------------------------------------------------------------ accept
export const AcceptDialog = ({
  open,
  onClose,
  meta,
  viewId,
  selection,
  plan,
  onAccepted,
}: {
  open: boolean;
  onClose: () => void;
  meta: ProposalMeta;
  viewId: string | null;
  selection: SelectionItem[];
  plan: Plan | null;
  onAccepted: (a: { at: string; name: string }) => void;
}) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const investment = meta.pricing ? quoteLines(quote(meta.pricing, plan)) : [];

  useEffect(() => {
    if (open) setError('');
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await api<{ accepted: { at: string; name: string } }>(`/api/proposals/${meta.slug}/accept`, {
        body: { viewId, name, roleTitle: role, confirm: true, selection, plan },
      });
      setDone(true);
      onAccepted(res.accepted);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} kicker={done ? 'Aceite registrado' : 'Aceitar proposta'} title={done ? 'Tudo certo.' : meta.title}>
      {done ? (
        <Done
          title={`Obrigado, ${name.trim().split(/\s+/)[0]}.`}
          text="A Ringa já foi avisada e entra em contato em até 1 dia útil para combinar o início. Uma cópia do aceite foi para o seu e-mail."
          onClose={onClose}
        />
      ) : (
        <form onSubmit={submit} className="accept-form">
          <dl className="facts">
            <div>
              <dt>Cliente</dt>
              <dd>{meta.clientName}</dd>
            </div>
            <div>
              <dt>Versão</dt>
              <dd>v{meta.versionNumber}</dd>
            </div>
            <div>
              <dt>Válida até</dt>
              <dd>{fmtDate(meta.expiresAt)}</dd>
            </div>
          </dl>
          {investment.length > 0 && (
            <div className="picked invest">
              <p className="picked-h">Investimento</p>
              <ul>
                {investment.map(([k, v], i) => (
                  <li key={k} className={i === 2 ? 'invest-total' : undefined}>
                    <span>{k}</span>
                    <strong>{v}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {selection.length > 0 && (
            <div className="picked">
              <p className="picked-h">Sua configuração</p>
              <ul>
                {selection.map((s, i) => (
                  <li key={i}>
                    {s.label && <span>{s.label}</span>}
                    <strong>{s.value}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="two">
            <label className="field">
              <span>Seu nome</span>
              <input className="input" required minLength={2} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="field">
              <span>Cargo</span>
              <input className="input" autoComplete="organization-title" value={role} onChange={(e) => setRole(e.target.value)} />
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} required />
            <span>Li a proposta e quero seguir com ela. Entendo que a Ringa vai registrar este aceite com a versão, a data e o meu e-mail.</span>
          </label>
          {error && <p className="error">{error}</p>}
          <div className="dialog-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Agora não
            </button>
            <button type="submit" className="btn" disabled={busy || !agree || name.trim().length < 2}>
              {busy ? <span className="spinner" style={{ width: 18, height: 18 }} /> : <Scramble>Confirmar aceite</Scramble>}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
};

// ------------------------------------------------------------------ adjust · meeting · renewal
const emptySlot = (offset: number): Slot => ({ date: todayIso(offset), period: 'manha' });

export const AdjustDialog = ({
  open,
  onClose,
  slug,
  viewId,
  sections,
  section,
  renewal,
}: {
  open: boolean;
  onClose: () => void;
  slug: string;
  viewId: string | null;
  sections: SectionInfo[];
  section: string | null;
  renewal?: boolean;
}) => {
  const [tab, setTab] = useState<'adjust' | 'meeting'>('adjust');
  const [message, setMessage] = useState('');
  const [where, setWhere] = useState('');
  const [slots, setSlots] = useState<Slot[]>([emptySlot(1)]);
  const [mode, setMode] = useState<'online' | 'presencial'>('online');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<'adjust' | 'meeting' | 'renewal' | null>(null);

  useEffect(() => {
    if (open) {
      setError('');
      setWhere(section ?? '');
    }
  }, [open, section]);

  const kind = renewal ? 'renewal' : tab;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api(`/api/proposals/${slug}/respond`, {
        body: {
          kind,
          viewId,
          message,
          section: kind === 'adjust' ? where || null : section,
          slots: kind === 'meeting' ? slots : [],
          mode: kind === 'meeting' ? mode : null,
        },
      });
      setDone(kind);
      setMessage('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    onClose();
    window.setTimeout(() => setDone(null), 300);
  };

  if (done) {
    const text =
      done === 'meeting'
        ? 'A Ringa confirma um dos horários em até 1 dia útil. Você recebeu uma cópia por e-mail.'
        : 'A Ringa responde em até 1 dia útil. Você recebeu uma cópia por e-mail.';
    return (
      <Dialog open={open} onClose={close} kicker="Pedido enviado" title="Recebemos.">
        <Done title={done === 'meeting' ? 'Reunião pedida.' : done === 'renewal' ? 'Renovação pedida.' : 'Ajuste pedido.'} text={text} onClose={close} />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      kicker={renewal ? 'Proposta vencida' : 'Falar com a Ringa'}
      title={renewal ? 'Pedir renovação' : tab === 'adjust' ? 'O que você ajustaria?' : 'Vamos conversar.'}
    >
      <form onSubmit={submit} className="adjust-form">
        {!renewal && (
          <div className="seg" role="group" aria-label="Tipo de pedido">
            <button type="button" aria-pressed={tab === 'adjust'} onClick={() => setTab('adjust')}>
              Pedir um ajuste
            </button>
            <button type="button" aria-pressed={tab === 'meeting'} onClick={() => setTab('meeting')}>
              Marcar uma reunião
            </button>
          </div>
        )}

        {kind === 'adjust' && sections.length > 0 && (
          <label className="field">
            <span>Sobre qual parte</span>
            <select className="select" value={where} onChange={(e) => setWhere(e.target.value)}>
              <option value="">A proposta como um todo</option>
              {sections.map((s) => (
                <option key={s.key} value={s.label}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {kind === 'meeting' && (
          <>
            <div className="field">
              <span>Sugira até 3 horários</span>
              <div className="slots">
                {slots.map((s, i) => (
                  <div className="slot" key={i}>
                    <input
                      className="input"
                      type="date"
                      aria-label={`Data ${i + 1}`}
                      min={todayIso(0)}
                      value={s.date}
                      required
                      onChange={(e) => setSlots(slots.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))}
                    />
                    <select
                      className="select"
                      aria-label={`Período ${i + 1}`}
                      value={s.period}
                      onChange={(e) => setSlots(slots.map((x, j) => (j === i ? { ...x, period: e.target.value as Period } : x)))}
                    >
                      {(Object.keys(PERIODS) as Period[]).map((p) => (
                        <option key={p} value={p}>
                          {PERIODS[p]}
                        </option>
                      ))}
                    </select>
                    {slots.length > 1 && (
                      <button type="button" className="icon-btn" aria-label={`Remover horário ${i + 1}`} onClick={() => setSlots(slots.filter((_, j) => j !== i))}>
                        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {slots.length < 3 && (
                <button type="button" className="btn-link" style={{ justifySelf: 'start' }} onClick={() => setSlots([...slots, emptySlot(slots.length + 1)])}>
                  + Outro horário
                </button>
              )}
            </div>
            <div className="seg" role="group" aria-label="Formato da reunião">
              <button type="button" aria-pressed={mode === 'online'} onClick={() => setMode('online')}>
                Online
              </button>
              <button type="button" aria-pressed={mode === 'presencial'} onClick={() => setMode('presencial')}>
                Presencial
              </button>
            </div>
          </>
        )}

        <label className="field">
          <span>{kind === 'adjust' ? 'Conte o que mudaria' : kind === 'meeting' ? 'Assunto (opcional)' : 'Mensagem (opcional)'}</span>
          <textarea
            className="textarea"
            required={kind === 'adjust'}
            minLength={kind === 'adjust' ? 3 : undefined}
            maxLength={4000}
            placeholder={
              kind === 'adjust'
                ? 'Ex.: dá para começar pelo Relatório de Obras e deixar a cotação para depois?'
                : kind === 'meeting'
                  ? 'Ex.: passar o cronograma com o time de engenharia.'
                  : 'Ex.: queremos retomar a conversa em novembro.'
            }
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="dialog-foot">
          <button type="button" className="btn btn-ghost" onClick={close}>
            Cancelar
          </button>
          <button type="submit" className="btn" disabled={busy}>
            {busy ? (
              <span className="spinner" style={{ width: 18, height: 18 }} />
            ) : (
              <Scramble>{kind === 'adjust' ? 'Enviar ajuste' : kind === 'meeting' ? 'Pedir reunião' : 'Pedir renovação'}</Scramble>
            )}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export const acceptedLabel = (a: { at: string; name: string }): string => `Aceita por ${a.name.split(/\s+/)[0]} em ${fmtDay(a.at)}`;
